import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowDown, ArrowUp, ClipboardList, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { adminButton } from '@/components/admin/ui';
import { parseItemLines } from '@/lib/checklists';
import type { TemplateDraft } from '@/hooks/useChecklists';

const templateSchema = z.object({
  name: z.string().trim().min(1, 'Give the checklist a name').max(120, 'Keep the name under 120 characters'),
  description: z.string().trim().max(500, 'Keep the description under 500 characters'),
  items: z
    .array(
      z.object({
        id: z.string().optional(),
        room: z.string().trim().max(80, 'Keep room names short'),
        label: z.string().trim().max(300, 'Keep each item under 300 characters'),
      }),
    )
    .refine((items) => items.some((item) => item.label.length > 0), 'Add at least one item'),
});

type TemplateValues = z.infer<typeof templateSchema>;

interface ChecklistTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: TemplateDraft | null;
  saving: boolean;
  onSave: (draft: TemplateDraft) => void;
}

const blankItem = (room = '') => ({ room, label: '' });

export function ChecklistTemplateDialog({ open, onOpenChange, initial, saving, onSave }: ChecklistTemplateDialogProps) {
  const form = useForm<TemplateValues>({
    resolver: zodResolver(templateSchema),
    defaultValues: { name: '', description: '', items: [blankItem()] },
  });
  const { fields, append, remove, move } = useFieldArray({ control: form.control, name: 'items' });
  const [paste, setPaste] = useState('');
  const [showPaste, setShowPaste] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.reset({
      name: initial?.name ?? '',
      description: initial?.description ?? '',
      items: initial?.items.length
        ? initial.items.map((item) => ({ id: item.id, room: item.room ?? '', label: item.label }))
        : [blankItem()],
    });
    setPaste('');
    setShowPaste(false);
  }, [open, initial, form]);

  const addPasted = () => {
    const lastRoom = form.getValues('items').at(-1)?.room ?? '';
    const parsed = parseItemLines(paste, lastRoom || null);
    if (parsed.length === 0) return;
    // Replace a single empty starter row rather than leaving it dangling.
    const current = form.getValues('items');
    if (current.length === 1 && !current[0].label.trim()) remove(0);
    append(parsed.map((item) => ({ room: item.room ?? '', label: item.label })));
    setPaste('');
    setShowPaste(false);
  };

  const submit = form.handleSubmit((values) =>
    onSave({
      id: initial?.id,
      name: values.name,
      description: values.description || null,
      items: values.items
        .filter((item) => item.label)
        .map((item) => ({ id: item.id, label: item.label, room: item.room || null })),
    }),
  );

  const itemsError = form.formState.errors.items?.root?.message ?? form.formState.errors.items?.message;

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="adm-form max-h-[90vh] max-w-2xl gap-0 overflow-y-auto rounded-2xl bg-card p-0">
        <DialogHeader className="border-b border-border px-6 py-4 text-left">
          <DialogTitle className="text-[16px] font-semibold">{initial?.id ? 'Edit checklist' : 'New checklist'}</DialogTitle>
          <DialogDescription className="text-[12.5px]">
            Items are ticked off by your cleaners in this order. Add a room to group items together.
          </DialogDescription>
        </DialogHeader>

        <form id="checklist-template-form" onSubmit={submit} className="space-y-5 px-6 py-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="checklist-name">Name</Label>
              <Input id="checklist-name" placeholder="e.g. Regular clean" {...form.register('name')} />
              {form.formState.errors.name && <p className="text-[12px] text-destructive">{form.formState.errors.name.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="checklist-description">Description (optional)</Label>
              <Input id="checklist-description" placeholder="Who or what it’s for" {...form.register('description')} />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label>Items</Label>
              <span className="text-[12px] text-muted-foreground">{fields.length} {fields.length === 1 ? 'item' : 'items'}</span>
            </div>
            <ol className="space-y-2">
              {fields.map((field, index) => (
                <li key={field.id} className="grid grid-cols-[1fr_auto] gap-2 sm:grid-cols-[150px_1fr_auto]">
                  <Input
                    aria-label={`Room for item ${index + 1}`}
                    placeholder="Room (optional)"
                    className="col-span-2 sm:col-span-1"
                    {...form.register(`items.${index}.room`)}
                  />
                  <Input
                    aria-label={`Item ${index + 1}`}
                    placeholder="e.g. Hoover and mop floors"
                    {...form.register(`items.${index}.label`)}
                  />
                  <div className="flex items-center gap-1">
                    <Button type="button" variant="ghost" size="sm" className={adminButton.iconGhost} aria-label={`Move item ${index + 1} up`} disabled={index === 0} onClick={() => move(index, index - 1)}>
                      <ArrowUp />
                    </Button>
                    <Button type="button" variant="ghost" size="sm" className={adminButton.iconGhost} aria-label={`Move item ${index + 1} down`} disabled={index === fields.length - 1} onClick={() => move(index, index + 1)}>
                      <ArrowDown />
                    </Button>
                    <Button type="button" variant="ghost" size="sm" className={adminButton.iconGhost} aria-label={`Remove item ${index + 1}`} onClick={() => remove(index)} disabled={fields.length === 1}>
                      <Trash2 />
                    </Button>
                  </div>
                </li>
              ))}
            </ol>
            {itemsError && <p className="text-[12px] text-destructive">{itemsError}</p>}
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className={adminButton.outline}
                onClick={() => append(blankItem(form.getValues('items').at(-1)?.room ?? ''))}
              >
                <Plus /> Add item
              </Button>
              <Button type="button" size="sm" variant="ghost" className={adminButton.ghost} onClick={() => setShowPaste((v) => !v)}>
                <ClipboardList /> Paste a list
              </Button>
            </div>
            {showPaste && (
              <div className="space-y-2 rounded-xl border border-border bg-muted/30 p-3">
                <Label htmlFor="checklist-paste" className="text-[12.5px]">
                  One item per line. Start a line with a room and a colon to group it, e.g. “Kitchen: Wipe worktops”.
                </Label>
                <Textarea id="checklist-paste" rows={5} value={paste} onChange={(e) => setPaste(e.target.value)} />
                <Button type="button" size="sm" className={adminButton.primary} onClick={addPasted} disabled={!paste.trim()}>
                  Add these items
                </Button>
              </div>
            )}
          </div>
        </form>

        <DialogFooter className="gap-2 border-t border-border bg-muted/30 px-6 py-3.5 sm:space-x-0">
          <Button type="button" variant="outline" size="sm" className={adminButton.outline} onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="checklist-template-form" size="sm" className={adminButton.primary} disabled={saving}>
            {saving && <Loader2 className="animate-spin" />}
            Save checklist
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
